import {
  CrossmintEvent,
  CrossmintPaymentElement,
  useCrossmintEvents,
} from '@crossmint/client-sdk-react-ui';
import { BigNumber } from '@ethersproject/bignumber';
import { formatEther } from '@ethersproject/units';
import { Check, PaperPlaneTilt } from '@phosphor-icons/react';
import { Suspense, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { claims } from '@manifoldxyz/claim-contracts';
import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { ClaimStudioInstance } from '@/api/models/studioInstance';
import XMarkIcon from '@/assets/manifold-icons/x.svg';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import { getExtensionAddress } from '@/common/util';
import ClaimExtensionContract from '@/contracts/claimExtensionContract';
import { useClaim } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import useMainStore from '@/store/mainStore';
import CheckoutPriceDetails from './CheckoutPriceDetails';
import Modal from './Modal';

/**
 * Processing Modal component that displays during transaction confirmation
 */
const ProcessingModal = () => {
  return (
    <div className="m-claim--my-auto m-claim--flex m-claim--h-full m-claim--flex-col m-claim--items-center m-claim--justify-center m-claim--gap-4 m-claim--text-center">
      <div className="m-claim--relative m-claim--animate-pulse">
        <div className="m-claim--size-16 m-claim--animate-spin m-claim--rounded-full m-claim--border-4 m-claim--border-default m-claim--border-t-transparent"></div>
        <PaperPlaneTilt className="m-claim--absolute m-claim--inset-0 m-claim--top-1.5 m-claim--m-auto m-claim--size-6 m-claim--animate-bounce m-claim--text-secondary" />
      </div>
      <h3 className="m-claim--text-xl m-claim--font-bold m-claim--text-primary">Processing</h3>
      <p className="m-claim--max-w-md m-claim--text-secondary">
        We are delivering your mint. This can take 1-2 minutes to complete.
        <strong className="m-claim--mt-2 m-claim--block m-claim--animate-pulse m-claim--text-primary">
          Please do not leave this page.
        </strong>
      </p>
    </div>
  );
};

// Loading fallback component
const CrossmintLoading = () => (
  <div className="m-claim--flex m-claim--min-h-80 m-claim--w-full m-claim--items-center m-claim--justify-center">
    <Skeleton
      height="500px"
      width="800px"
    />
  </div>
);

/**
 * Validates an Ethereum address
 * @param address - The Ethereum address to validate
 * @returns Whether the address is valid
 */
const isValidEthereumAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Used for doing the Crossmint transactions to claim.
 * Cannot be used for secondary token purchasing.
 * Has not been tested or made to work with claims that require ERC20 payments.
 * Has not been tested to work with Gated Claims as there is no input for
 * the redeem code, and wallet address is not directly connected through
 * Manifold Ethereum Provider so... you're relying on the Crossmint SDK to
 * fill out the mintToAddress field. That makes it impossible for us to know
 * when it's filled out and we can request a merkle proof.
 */
export default function CrossmintTransactionsFlow({
  onSuccess,
  onClose,
}: {
  onSuccess: () => Promise<void>;
  onClose: () => void;
}) {
  // main store
  const studioInstance = useMainStore((state) => state.studioInstance);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const walletAddress = useMainStore((state) => state.walletAddress);
  const mintToAddress = useMainStore((state) => state.mintToAddress);
  const setMintToAddress = useMainStore((state) => state.setMintToAddress);
  const setMintedTxHash = useMainStore((state) => state.setMintedTxHash);
  const setMintedTokenIds = useMainStore((state) => state.setMintedTokenIds);
  const { listenToMintingEvents } = useCrossmintEvents({
    // @ref: https://docs.crossmint.com/introduction/platform/staging-vs-production
    environment: studioInstance?.publicData.network === 11155111 ? 'staging' : 'production',
  });
  const { refreshClaim } = useClaim();
  const analytics = useAnalytics();

  // component state
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [isAddressValid, setIsAddressValid] = useState<boolean>(true);
  const [hasConfirmingTx, setHasConfirmingTx] = useState<boolean>(false);
  // random constants
  const crossmintClientId = studioInstance?.publicData.crossmintClientId || '';
  const assetName = studioInstance?.publicData.asset?.name || 'Token';

  const handleWalletAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;

    setMintToAddress(address);

    if (address === '') {
      // Empty is valid (optional field)
      setIsAddressValid(true);
    } else {
      // Validate address if provided
      setIsAddressValid(isValidEthereumAddress(address));
    }
  };

  const mintingEvents = (event: CrossmintEvent) => {
    switch (event.type) {
      case 'order:process.finished':
        // We are completely done
        setHasConfirmingTx(false);
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.CROSSMINT_TX_SUCCESS,
        });
        refreshClaim();
        onSuccess();
        break;
      case 'transaction:fulfillment.succeeded':
        // The minting process has finished and we have info about it
        setMintedTxHash(event.payload.txId);
        // tokenIds is missing in the type def for some reason
        if ('tokenIds' in event.payload) {
          setMintedTokenIds(event.payload.tokenIds);
        }
        break;
      case 'transaction:fulfillment.failed':
        // A transaction has failed
        setError(event.payload.error.message);
        setHasConfirmingTx(false);
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.CROSSMINT_TX_FAIL,
        });
        break;
      case 'order:process.started':
        // Capturing NFTs process has started
        break;
    }
  };

  const onEvent = (event: CrossmintEvent) => {
    switch (event.type) {
      case 'payment:process.started':
        // Triggered when the user has finished entering their card details and clicked Pay.
        // let's show a loading state here and disable the X button
        setHasConfirmingTx(true);
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.CROSSMINT_TX_ATTEMPT,
        });
        break;
      case 'payment:process.succeeded':
        // Triggered when the user has successfully decided to pay for the order and start minting
        if (event.payload.orderIdentifier) {
          listenToMintingEvents({ orderIdentifier: event.payload.orderIdentifier }, mintingEvents);
        }
        break;
      case 'payment:process.rejected':
        // Triggered if a user's card has been rejected.
        setError(event.payload.error.message);
        setHasConfirmingTx(false);
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.CROSSMINT_TX_FAIL,
        });
        break;
      case 'payment:preparation.failed':
        // Triggered if a failure has occurred after checkout.
        setError(event.payload.error.message);
        setHasConfirmingTx(false);
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.CROSSMINT_TX_FAIL,
        });
        break;
      default:
        break;
    }
  };

  // Calculate price
  useEffect(() => {
    const asyncHandler = async () => {
      if (!studioInstance) return;

      // Crossmint requires ETH as the mint price currency
      if (studioInstance.publicData.mintPrice.currency === 'ETH') {
        let value = BigNumber.from(0);
        if (studioInstance.publicData.mintPrice.symbol !== 'ETH') {
          throw new Error('Crossmint only supports ETH as the mint price currency');
        } else {
          value = BigNumber.from(studioInstance.publicData.mintPrice.value).mul(numberOfMints);
        }

        const claimContract = new ClaimExtensionContract(
          studioInstance.publicData.network,
          getExtensionAddress(studioInstance),
          studioInstance.publicData.contract.contractAddress,
          parseInt(
            (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
              studioInstance.id,
          ),
        );

        const extensionContractAddress = getExtensionAddress(studioInstance);
        const ext = claims.getExtension(extensionContractAddress) as claims.ClaimDeployment;

        const feeToUse = await claimContract.getMintFee();
        if (ext && claims.hasTrait(ext, claims.Trait.Fee)) {
          value = value.add(feeToUse.mul(numberOfMints));
        }

        setPrice(value.toString());
      }
    };

    asyncHandler();
  }, [studioInstance, numberOfMints, mintToAddress, walletAddress]);

  if (!studioInstance || !price) {
    return (
      <Skeleton
        height="500px"
        width="100%"
      />
    );
  }

  /**
   * Document all the times you should not people to use this component
   */
  if (!crossmintClientId) {
    throw new Error('Crossmint client ID is not set');
  }
  if (studioInstance.publicData.mintPrice.currency !== 'ETH') {
    throw new Error('Crossmint only supports ETH as the mint price currency');
  }
  if (studioInstance.publicData.mintingRestriction === 'Allowlist') {
    throw new Error('Crossmint does not support any gated/restricted mints');
  }
  if (studioInstance.publicData.mintingRestriction === 'RedemptionCodes') {
    throw new Error('Crossmint does not support redemption codes');
  }

  const instanceId =
    (studioInstance as ClaimStudioInstance).publicData?.claimIndex || studioInstance.id;

  if (!instanceId) {
    throw new Error('Instance ID is not set');
  }

  const extensionContractAddress = getExtensionAddress(studioInstance);

  const mintConfig = {
    // @dev: required params for the contract call onchain
    type: studioInstance.publicData.contract.spec.toLowerCase(),
    totalPrice: formatEther(price),
    creatorContractAddress: studioInstance.publicData.contract.contractAddress.toLowerCase(),
    instanceId: instanceId,
    mintCount: numberOfMints,
    // @dev: we do not support gated claims yet in Crossmint so we can hardcode blank values here
    mintIndices: [],
    merkleProofs: [],
    // @dev: required so Crossmint knows what contract we want to hit
    contractAddress: extensionContractAddress,
  };

  return (
    <div className="m-claim--flex m-claim--size-full m-claim--flex-col">
      <button
        onClick={onClose}
        className={StringUtils.classnames(
          'm-claim--absolute',
          'm-claim--right-0',
          'm-claim--top-0',
          'm-claim--p-2',
          'm-claim--text-secondary',
          'm-claim--transition-colors',
          'm-claim--z-[60]', // Added higher z-index
          'm-claim--bg-surface-secondary', // Added background color
          'm-claim--rounded-full', // Added full rounded border radius
          'hover:m-claim--bg-surface-accent',
          hasConfirmingTx && 'm-claim--opacity-50',
        )}
        style={{
          outline: '2px solid transparent',
          outlineOffset: '2px',
        }}
        aria-label="Close modal"
        disabled={hasConfirmingTx}
      >
        <img
          src={XMarkIcon}
          alt="Close"
          className="m-claim--size-6"
        />
      </button>

      {/* Processing Modal */}
      <Modal
        isOpen={hasConfirmingTx}
        onClose={() => {}}
        disableCloseButton={true}
        size="sm"
      >
        <ProcessingModal />
      </Modal>

      {/* Fixed Header */}
      <div className="m-claim--mb-4 m-claim--shrink-0 m-claim--text-xl m-claim--font-bold m-claim--text-primary">
        Payment
      </div>

      {/* Main Content */}
      <div className="m-claim--grow m-claim--overflow-y-auto">
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-6">
          <div className="">
            {error && (
              <div className="m-claim--mb-4 m-claim--rounded-md m-claim--bg-surface-destructive m-claim--p-3 m-claim--text-sm m-claim--text-destructive">
                {error}
              </div>
            )}

            <div className="m-claim--flex m-claim--w-full m-claim--flex-col m-claim--gap-4">
              {/* Crossmint Payment Element */}
              {crossmintClientId && studioInstance && price ? (
                <div className="m-claim--rounded-md m-claim--bg-surface-secondary m-claim--p-4">
                  <Suspense fallback={<CrossmintLoading />}>
                    {/* Custom Wallet Address Input */}
                    <div className="m-claim--mb-4">
                      <label
                        htmlFor="wallet-address"
                        className="m-claim--mb-1 m-claim--block m-claim--text-sm m-claim--font-medium m-claim--text-primary"
                      >
                        <span className="m-claim--flex m-claim--items-center m-claim--gap-1">
                          Ethereum Wallet (Optional)
                          {isAddressValid && mintToAddress && (
                            <Check className="m-claim--mb-1 m-claim--text-success" />
                          )}
                        </span>
                      </label>
                      <input
                        id="wallet-address"
                        type="text"
                        placeholder="0x..."
                        value={mintToAddress}
                        onChange={handleWalletAddressChange}
                        className={`m-claim--w-full m-claim--border m-claim--border-default m-claim--p-3 m-claim--text-sm m-claim--text-primary m-claim--shadow-sm ${
                          !isAddressValid && mintToAddress ? 'm-claim--border-[#ef4444]' : ''
                        }`}
                        style={{
                          outline: '2px solid transparent',
                          outlineOffset: '2px',
                        }}
                        aria-label="Ethereum wallet address"
                      />
                      {!isAddressValid && (
                        <p className="m-claim--mt-1 m-claim--text-sm m-claim--font-semibold m-claim--text-destructive">
                          Please enter a valid Ethereum address
                        </p>
                      )}
                    </div>
                    <CrossmintPaymentElement
                      collectionId={crossmintClientId}
                      mintConfig={mintConfig}
                      environment={
                        // @ref: https://docs.crossmint.com/introduction/platform/staging-vs-production
                        studioInstance.publicData.network === 11155111 ? 'staging' : 'production'
                      }
                      emailInputOptions={{ show: true }}
                      uiConfig={{
                        colors: {
                          background: '#1c1c21',
                          backgroundSecondary: '#1c1c21',
                          backgroundTertiary: 'white',
                          textPrimary: 'white',
                          textSecondary: 'black',
                          textLink: 'gray',
                          accent: 'white',
                          border: '#404040',
                          danger: 'red',
                        },
                        fontWeightSecondary: 'bold',
                        borderRadius: '0px',
                        spacingUnit: '0px',
                      }}
                      // @ts-expect-error - bad typing from old Crossmint SDK
                      recipient={mintToAddress ? { wallet: mintToAddress } : null}
                      onEvent={onEvent}
                    />
                  </Suspense>
                </div>
              ) : (
                <CrossmintLoading />
              )}
            </div>

            <div className="m-claim--mt-4 m-claim--flex m-claim--shrink-0 m-claim--flex-col m-claim--gap-1 m-claim--rounded-md m-claim--bg-surface-secondary m-claim--p-4">
              <div>
                <h1 className="m-claim--mb-3 m-claim--text-lg m-claim--font-bold m-claim--text-primary">
                  Order Summary
                </h1>

                <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
                  <div className="m-claim--grid m-claim--grid-cols-2 m-claim--gap-2 m-claim--text-sm">
                    <span className="m-claim--overflow-hidden m-claim--text-ellipsis m-claim--text-sm m-claim--text-primary">
                      {StringUtils.abbreviate(assetName, 20, 0)}
                    </span>
                    <span className="m-claim--text-right m-claim--text-sm m-claim--text-primary">
                      {numberOfMints}x
                    </span>
                  </div>

                  <CheckoutPriceDetails />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
